import { useState, useEffect } from 'react';
import api from '../services/api';
import './SheetViewer.css';

/**
 * SHEET VIEWER - Displays Google Sheet data with smart parsing
 * Automatically separates stats section from trade data
 * Matches TradeTracker styling
 */
function SheetViewer({ guruSlug }) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [sheetData, setSheetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStats, setShowStats] = useState(true);

  // Fetch products with sheet_url
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get(`/gurus/${guruSlug}/products`);
        const trackingProducts = response.data.filter(p => p.sheet_url);
        setProducts(trackingProducts);
        if (trackingProducts.length > 0) {
          setSelectedProduct(trackingProducts[0].id);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [guruSlug]);

  // Fetch sheet data when product changes
  useEffect(() => {
    if (!selectedProduct) return;

    const fetchSheetData = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('📊 Fetching sheet data...');
        const response = await api.get(`/products/${selectedProduct}/sheet-html`);
        const parsed = parseSheetData(response.data.rows);
        setSheetData({ ...response.data, ...parsed });
        console.log(`✓ Loaded ${response.data.rows?.length || 0} rows`);
      } catch (err) {
        console.error('Error fetching sheet:', err);
        setError('Failed to load sheet data');
        setSheetData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSheetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  /**
   * Parse sheet data to separate stats from trades
   * Detects header row by looking for common column names
   */
  const parseSheetData = (rows) => {
    if (!rows || rows.length === 0) return { stats: [], headers: [], trades: [] };

    // Common header patterns to detect trade table start
    const headerPatterns = ['symbol', 'entry', 'exit', 'date', 'option', 'price', 'profit', 'p/l', 'trade'];

    let headerRowIndex = -1;

    // Find the header row
    for (let i = 0; i < rows.length && i < 20; i++) {
      const row = rows[i];
      const rowText = row.join(' ').toLowerCase();
      const matchCount = headerPatterns.filter(p => rowText.includes(p)).length;

      // If row has 3+ header pattern matches and has 5+ cells, it's likely the header
      if (matchCount >= 3 && row.filter(c => c && c.trim()).length >= 5) {
        headerRowIndex = i;
        break;
      }
    }

    if (headerRowIndex === -1) {
      // No clear header found, treat all as raw data
      return { stats: [], headers: [], trades: rows, isRawMode: true };
    }

    // Everything before header is stats
    const statsRows = rows.slice(0, headerRowIndex).filter(row =>
      row.some(cell => cell && cell.trim())
    );

    // Header row
    const headers = rows[headerRowIndex];

    // Everything after header is trades
    const tradeRows = rows.slice(headerRowIndex + 1).filter(row =>
      row.some(cell => cell && cell.trim())
    );

    // Parse stats into structured format
    const parsedStats = parseStats(statsRows);

    return {
      stats: parsedStats,
      headers,
      trades: tradeRows,
      isRawMode: false
    };
  };

  /**
   * Parse stats rows into a structured format
   * Extracts key-value pairs from various formats
   */
  const parseStats = (statsRows) => {
    const stats = {
      title: '',
      subtitle: '',
      metrics: []
    };

    statsRows.forEach((row, idx) => {
      const cells = row.filter(c => c && c.trim());

      if (cells.length === 0) return;

      // First row with single cell is likely the title
      if (idx === 0 && cells.length === 1) {
        stats.title = cells[0];
        return;
      }

      // Second row with single cell might be subtitle
      if (idx <= 2 && cells.length === 1 && !stats.subtitle) {
        stats.subtitle = cells[0];
        return;
      }

      // Parse key-value pairs from the row
      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];

        // Check if this cell is a label (ends with : or next cell is a value)
        if (cell.endsWith(':')) {
          const label = cell.slice(0, -1).trim();
          const value = cells[i + 1] || '';
          if (value && !value.endsWith(':')) {
            stats.metrics.push({ label, value, type: getMetricType(value) });
            i++; // Skip the value cell
          }
        }
        // Check for "Label Value" pattern in same cell or adjacent
        else if (i + 1 < cells.length) {
          const nextCell = cells[i + 1];
          // If current looks like label and next looks like value
          if (isLabel(cell) && isValue(nextCell)) {
            stats.metrics.push({ label: cell, value: nextCell, type: getMetricType(nextCell) });
            i++;
          }
        }
      }
    });

    return stats;
  };

  const isLabel = (str) => {
    if (!str) return false;
    // Labels typically don't have $ or % at the start
    return !str.startsWith('$') && !str.startsWith('-$') &&
           !str.match(/^[\d.]+%/) && !str.match(/^\$[\d,]+/);
  };

  const isValue = (str) => {
    if (!str) return false;
    // Values typically have $ or % or are numbers
    return str.includes('$') || str.includes('%') || !isNaN(parseFloat(str.replace(/[,$]/g, '')));
  };

  const getMetricType = (value) => {
    if (!value) return 'neutral';
    if (value.includes('-') || value.includes('(')) return 'negative';
    if (value.includes('%') && parseFloat(value) > 0) return 'positive';
    if (value.includes('$') && !value.includes('-')) return 'positive';
    return 'neutral';
  };

  // Determine cell styling based on content
  const getCellClass = (value) => {
    if (!value) return 'sheet-cell';
    if (value.includes('-') && (value.includes('$') || value.includes('%'))) {
      return 'sheet-cell negative';
    }
    if ((value.includes('$') || value.includes('%')) && !value.includes('-')) {
      const num = parseFloat(value.replace(/[$,%\s]/g, ''));
      if (!isNaN(num) && num > 0) return 'sheet-cell positive';
    }
    return 'sheet-cell';
  };

  // Render cell content with line breaks
  const renderCell = (cell) => {
    if (!cell) return '';
    if (cell.includes('\n')) {
      return cell.split('\n').map((line, i) => (
        <span key={i}>
          {line}
          {i < cell.split('\n').length - 1 && <br />}
        </span>
      ));
    }
    return cell;
  };

  const currentProduct = products.find(p => p.id === selectedProduct);

  if (loading && products.length === 0) {
    return <div className="sheet-loading">Loading Sheet Viewer...</div>;
  }

  if (products.length === 0) {
    return <div className="sheet-empty">No products with sheets available.</div>;
  }

  return (
    <div className="sheet-viewer">
      {/* Product Cards Grid - Matching TradeTracker style */}
      <div className="sv-products-grid">
        {products.map(product => (
          <div
            key={product.id}
            className={`sv-product-card ${selectedProduct === product.id ? 'active' : ''}`}
            onClick={() => setSelectedProduct(product.id)}
          >
            {product.logo_url ? (
              <img src={product.logo_url} alt={product.name} className="sv-product-logo" />
            ) : (
              <div className="sv-product-logo-placeholder">
                {product.name.substring(0, 2).toUpperCase()}
              </div>
            )}
            <div className="sv-product-name">{product.name}</div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="sheet-loading">Loading sheet data...</div>
      ) : error ? (
        <div className="sheet-error">{error}</div>
      ) : sheetData ? (
        <div className="sv-content">
          {/* Product Header */}
          {currentProduct && (
            <div className="sv-selected-header">
              {currentProduct.logo_url ? (
                <img src={currentProduct.logo_url} alt={currentProduct.name} className="sv-selected-logo" />
              ) : (
                <div className="sv-selected-logo-placeholder">
                  {currentProduct.name.substring(0, 2).toUpperCase()}
                </div>
              )}
              <div className="sv-selected-info">
                <h3>{sheetData.stats?.title || currentProduct.name}</h3>
                <p>{sheetData.stats?.subtitle || 'Live Sheet Data'}</p>
                <span className="sv-live-badge">LIVE</span>
              </div>
            </div>
          )}

          {/* Stats Section */}
          {sheetData.stats?.metrics?.length > 0 && (
            <div className="sv-stats-section">
              <button
                className="sv-stats-toggle"
                onClick={() => setShowStats(!showStats)}
              >
                <span className="sv-stats-icon">📊</span>
                <span className="sv-stats-text">
                  Performance Statistics
                  {sheetData.stats.metrics.find(m => m.label.toLowerCase().includes('win')) && (
                    <span className="sv-win-rate">
                      {sheetData.stats.metrics.find(m =>
                        m.label.toLowerCase().includes('win') && m.value.includes('%')
                      )?.value}
                    </span>
                  )}
                </span>
                <span className={`sv-stats-arrow ${showStats ? 'open' : ''}`}>▼</span>
              </button>

              {showStats && (
                <div className="sv-stats-container">
                  <div className="sv-stats-grid">
                    {sheetData.stats.metrics.map((metric, idx) => (
                      <div key={idx} className="sv-stat-card">
                        <div className="sv-stat-label">{metric.label}</div>
                        <div className={`sv-stat-value ${metric.type}`}>{metric.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Trades Table */}
          <div className="sv-table-container">
            <div className="sv-table-header">
              <span>Updated: {new Date(sheetData.fetched_at).toLocaleTimeString()}</span>
              <span>{sheetData.trades?.length || 0} trades</span>
            </div>

            {sheetData.headers && sheetData.headers.length > 0 && !sheetData.isRawMode ? (
              <table className="sv-table">
                <thead>
                  <tr>
                    {sheetData.headers.map((header, idx) => (
                      <th key={idx}>{renderCell(header)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sheetData.trades?.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} className={getCellClass(cell)}>
                          {renderCell(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              // Raw mode - no header detected
              <table className="sv-table raw-mode">
                <tbody>
                  {sheetData.rows
                    ?.filter(row => row.some(cell => cell && cell.trim()))
                    .map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx} className={getCellClass(cell)}>
                            {renderCell(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : (
        <div className="sheet-empty">No data available</div>
      )}
    </div>
  );
}

export default SheetViewer;
